import {RegisterWithEmailV1Response} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.types';
import {useTranslation} from '@aboutyou/i18n';
import {AuthenticationEvent, Log} from '@aboutyou/mlm';
import {useTrackAuthentication} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import type {AuthMedium, LoggedInUserDataWithToken} from '@aboutyou/user';
import {doAsync} from '@aboutyou/utils';
import {useCallback, useState} from 'react';
import {useSavedUser} from '../AuthenticationModal/hooks/useSavedUser';
import {AuthMethod} from '../types';
import {
  AuthResponseTypes,
  normalizeAuthData,
} from '../utility/normalizeAuthData';
import {AUTH_FLOW_STATE} from './state';
import {useLinkGuestSessionToCustomerSession} from './useLinkGuestSessionToCustomerSession';
import {useOnLoginSuccess} from './useOnLoginSuccess';

export const useAuthResponseHandler = ({
  disableSuccessToast,
}: {
  disableSuccessToast: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const linkSessions = useLinkGuestSessionToCustomerSession();
  const {saveUser} = useSavedUser();
  const onLoginCallback = useOnLoginSuccess();
  const trackAuthentication = useTrackAuthentication();
  const showToast = useShowToast();

  const handleAuthResponse = useCallback(
    ({method, medium}: {method: AuthMethod; medium: AuthMedium}) =>
      async (data: AuthResponseTypes): Promise<LoggedInUserDataWithToken> => {
        try {
          const {customerData, apiToken: authToken} = normalizeAuthData(data);

          await linkSessions(authToken);

          if (method !== 'guest-register') {
            /**
             * store the last saved user only as a side effect of successful login
             * Immediately setting the state here results in showing the RecognizedUser View while the modal is closing with animation.
             */
            doAsync(() => {
              saveUser({
                name: customerData.firstName,
                email: customerData.email,
                loginType: medium,
              });
            });
          }

          onLoginCallback({
            method,
            loginType: medium,
            authData: {
              customerData,
              apiToken: authToken,
            },
          });
          trackAuthentication({
            type: method === 'login' ? 'login' : 'sign_up',
            action: 'successful',
            email: customerData.email,
            customer_id: customerData.id.toString(),
            label: medium,
          });

          if (!disableSuccessToast) {
            const successText: string | undefined =
              method === 'register' &&
              !(data as RegisterWithEmailV1Response).isAlreadyRegistered
                ? t('Registration was successful. Welcome to About You!')
                : t('Success');

            showToast({
              type: 'SUCCESS',
              content: successText,
            });
          }

          Log.compare({
            actionName: AuthenticationEvent,
            success: true,
            attributes: {
              method,
              medium,
            },
          });

          return {customerData, authToken};
        } catch (error) {
          trackAuthentication({
            type: method === 'login' ? 'login' : 'sign_up',
            action: 'error',
            label: medium,
          });

          const metadata = getAuthErrorMetadata(error);

          Log.compare({
            actionName: AuthenticationEvent,
            success: false,
            attributes: {
              method,
              medium,
              code: metadata.code,
              reason: metadata.message,
              metadata,
            },
          });
          showToast({
            type: 'ERROR',
            content: t('Uh oh, something went wrong.'),
          });

          throw new SkipErrorLogging(
            "Couldn't handle auth response successfully",
          );
        } finally {
          setLoading(false);

          // scroll to the top since the product streams would refetch and if you are on high page number, the refetch would put you on the footer of the page
          window.scrollTo(0, 0);

          AUTH_FLOW_STATE.completedInThisSession = true;
        }
      },
    [
      linkSessions,
      onLoginCallback,
      trackAuthentication,
      disableSuccessToast,
      saveUser,
      t,
      showToast,
    ],
  );

  return {handleAuthResponse, loading, setLoading};
};

export const getAuthErrorMetadata = (error: any) => {
  const code = error.code ?? error.status ?? error.statusCode;

  return {
    code,
    // From the logs it looks like the error.message is empty when the grpc error code is `2`
    // Add that here to avoid empty error message in the logs. Includes the error code in the message.
    message: error.message || `Unknown error occurred. Error Code: ${code}`,
    stack: error.stack,
    error,
  };
};

class AuthHandlerError extends Error {
  code?: number;
  constructor(message: string | number) {
    super(`${message}`);
    Object.setPrototypeOf(this, AuthHandlerError.prototype);
  }
}

/**
 * This error should not lead to additional logging of `AuthenticationEvent` failure, in hooks like useEmailLoginHandler, etc.
 * because its already logged in the catch block of `useAuthResponseHandler`.
 * We throw this error still to stop the execution of onSuccess callback in the above mentioned login medium related hooks.
 */
export class SkipErrorLogging extends AuthHandlerError {
  constructor(message: string | number) {
    super(message);
    this.name = 'SkipErrorLogging';
    Object.setPrototypeOf(this, SkipErrorLogging.prototype);
  }
}
